/**
 * SHARED REACT ERROR BOUNDARY — UI crash protection.
 * Canonical copy lives in /shared/frontend/. Portal copies are synced into
 * <portal>/frontend/src/shared/ by scripts/sync-shared-frontend.sh — EDIT THE
 * CANONICAL COPY, then re-run the sync script.
 *
 * Usage (main.tsx / index.tsx):
 *   <ErrorBoundary><App /></ErrorBoundary>
 */
import React from 'react';

type Props = { children: React.ReactNode; fallback?: React.ReactNode };
type State = { hasError: boolean; error: Error | null };

export default class ErrorBoundary extends React.Component<Props, State> {
  state: State = { hasError: false, error: null };

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    // STRICT REQUIREMENT: component stack trace must reach the browser console.
    console.error('[ErrorBoundary] UI crash caught:', error);
    console.error('[ErrorBoundary] Component stack:', info.componentStack);
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) return this.props.fallback;
      return (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '60vh', fontFamily: 'system-ui, sans-serif', padding: 24, textAlign: 'center' }}>
          <h1 style={{ fontSize: 22, marginBottom: 8 }}>Something went wrong</h1>
          <p style={{ color: '#666', marginBottom: 4 }}>The page crashed unexpectedly. The full error has been logged to the browser console (F12).</p>
          <pre style={{ background: '#fef2f2', color: '#b91c1c', padding: 12, borderRadius: 8, maxWidth: 640, overflow: 'auto', fontSize: 12 }}>
            {this.state.error?.message}
          </pre>
          <button onClick={() => window.location.reload()} style={{ marginTop: 16, padding: '8px 20px', borderRadius: 8, border: 'none', background: '#2563eb', color: '#fff', cursor: 'pointer' }}>
            Reload page
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}
